import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api, { cacheGet, cacheSet, listFrom } from '../api';

const CLOUD_EXCLUDE = 'Cloud Popular';

function hostOf(item) {
  return item?.host || item || '';
}

export default function Filters() {
  const cached = cacheGet('filters', null);
  const [sets, setSets] = useState(() => cached?.sets || []);
  const [active, setActive] = useState(() => cached?.active || null);
  const [items, setItems] = useState(() => cached?.items || []);
  const [cloud, setCloud] = useState(() => cached?.cloud || []);
  const [custom, setCustom] = useState('');
  const [loading, setLoading] = useState(false);
  const activeRef = useRef(active);
  activeRef.current = active;

  const loadCloud = useCallback(async () => {
    const { data } = await api.get('/filters/cloud');
    const raw = listFrom(data, 'items', 'hosts');
    const seen = new Set();
    const hosts = [];
    for (const it of raw) {
      const host = hostOf(it);
      if (!host || seen.has(host)) continue;
      seen.add(host);
      hosts.push({ host, kind: it.kind || 'cookie' });
    }
    setCloud(hosts);
    return hosts;
  }, []);

  const openSet = useCallback(async (id) => {
    setActive(id);
    const { data } = await api.get(`/filters/${id}`);
    const next = listFrom(data, 'items');
    setItems(next);
    return next;
  }, []);

  const loadSets = useCallback(async (preferId) => {
    const { data } = await api.get('/filters');
    const list = listFrom(data, 'filters').filter((s) => s.name !== CLOUD_EXCLUDE);
    setSets(list);
    const pick = preferId || activeRef.current || list[0]?.id;
    let nextItems = [];
    if (pick) nextItems = await openSet(pick);
    else {
      setActive(null);
      setItems([]);
    }
    return { list, pick, nextItems };
  }, [openSet]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadSets(), loadCloud()])
      .then(([setsRes, hosts]) => {
        cacheSet('filters', {
          sets: setsRes.list,
          active: setsRes.pick || null,
          items: setsRes.nextItems || [],
          cloud: hosts,
        });
      })
      .catch(() => toast.error('Ошибка фильтров'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function createSet() {
    const name = window.prompt('Название набора фильтров', 'Мой фильтр');
    if (!name?.trim()) return;
    try {
      const { data } = await api.post('/filters', { name: name.trim() });
      toast.success('Набор создан');
      await loadSets(data.id);
    } catch {
      toast.error('Не удалось создать набор');
    }
  }

  async function deleteSet() {
    if (!active) return;
    if (!window.confirm('Удалить текущий набор фильтров?')) return;
    try {
      await api.delete(`/filters/${active}`);
      toast.success('Набор удалён');
      setActive(null);
      await loadSets();
    } catch {
      toast.error('Не удалось удалить');
    }
  }

  async function saveItems(next) {
    if (!active) {
      toast.error('Сначала создайте или выберите набор');
      return;
    }
    const payload = next.map((i) => ({ host: hostOf(i), kind: i.kind || 'cookie' }));
    await api.put(`/filters/${active}`, { items: payload });
    setItems(payload);
  }

  function addHost(host, kind = 'cookie') {
    const h = host.trim();
    if (!h) return;
    if (items.some((i) => hostOf(i) === h)) {
      toast('Уже добавлено');
      return;
    }
    const next = [...items, { host: h, kind }];
    saveItems(next)
      .then(() => toast.success(h))
      .catch(() => toast.error('Ошибка сохранения'));
  }

  function removeHost(host) {
    saveItems(items.filter((i) => hostOf(i) !== host)).catch(() => toast.error('Ошибка сохранения'));
  }

  const activeSet = sets.find((s) => s.id === active);

  return (
    <div>
      <div className="rm-page-head">
        <div className="rm-page-title">
          Фильтры
          {activeSet ? (
            <span style={{ fontWeight: 500, fontSize: 18, color: '#64748B' }}>— {activeSet.name}</span>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="rm-btn success" onClick={createSet}>+ Новый набор</button>
          {active ? (
            <button type="button" className="rm-btn danger" onClick={deleteSet}>Удалить набор</button>
          ) : null}
          <span className="rm-badge">выбрано {items.length} запросов</span>
        </div>
      </div>

      {sets.length > 0 && (
        <div className="rm-tags" style={{ marginBottom: 14 }}>
          {sets.map((s) => (
            <button
              key={s.id}
              type="button"
              className="rm-tag"
              style={{
                cursor: 'pointer',
                borderColor: s.id === active ? '#3B82F6' : undefined,
                background: s.id === active ? '#EFF6FF' : undefined,
              }}
              onClick={() => openSet(s.id).catch(() => toast.error('Ошибка загрузки'))}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {!sets.length && !loading && (
        <div className="rm-card rm-empty" style={{ marginBottom: 14 }}>
          Нет наборов фильтров. Создайте первый набор.
        </div>
      )}

      <div className="rm-card rm-selected-box">
        <b style={{ display: 'block', marginBottom: 10 }}>Выбранные запросы</b>
        <div className="rm-tags">
          {items.map((i) => {
            const host = hostOf(i);
            return (
              <span className="rm-tag" key={host}>
                {host}
                <button type="button" onClick={() => removeHost(host)}>×</button>
              </span>
            );
          })}
          <form
            style={{ display: 'inline-flex', gap: 6 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (!custom.trim()) return;
              addHost(custom.trim());
              setCustom('');
            }}
          >
            <input
              className="rm-search"
              style={{ minWidth: 160 }}
              placeholder="Свой запрос"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              disabled={!active}
            />
            <button type="submit" className="rm-btn success" disabled={!active}>✓</button>
          </form>
        </div>
      </div>

      <div className="rm-card" style={{ padding: 16 }}>
        <b>Облако запросов</b>
        <p style={{ color: '#64748B', fontSize: 13, margin: '8px 0 14px' }}>
          Популярные запросы — нажмите, чтобы добавить в текущий набор
        </p>
        <div className="rm-cloud">
          {cloud.map((it) => {
            const host = hostOf(it);
            return (
              <button
                key={host}
                type="button"
                className="rm-cloud-item"
                disabled={!active}
                onClick={() => addHost(host, it.kind || 'cookie')}
              >
                {host}
              </button>
            );
          })}
          {!cloud.length && !loading && <span style={{ color: '#94A3B8' }}>Облако пусто</span>}
        </div>
      </div>
    </div>
  );
}
